import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, documentId } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuthContext } from '../context/AuthContext';
import { useFriendRequests } from '../hooks/useFriendRequests';
import { useSentRequests } from '../hooks/useSentRequests';
import { useFriendsList } from '../hooks/useFriendsList';
import {
  sendFriendRequest,
  acceptFriendRequest,
  rejectFriendRequest,
  cancelFriendRequest,
  removeFriend,
} from '../services/friendService';
import { BADGES } from '../utils/constants';
import ProfileSubNav from '../components/ProfileSubNav';
import toast from 'react-hot-toast';
import './ProfilePage.css';
import './FriendsPage.css';

export default function FriendsPage() {
  const { user } = useAuthContext();
  const { requests, loading: requestsLoading } = useFriendRequests();
  const { sentRequests, loading: sentLoading } = useSentRequests();
  const { friends, loading: friendsLoading } = useFriendsList();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Action loading states
  const [actionLoading, setActionLoading] = useState({});

  const performSearch = useCallback(async (prefix) => {
    if (!prefix || prefix.length < 2) {
      setSearchResults([]);
      setHasSearched(true);
      return;
    }

    setSearching(true);
    setHasSearched(true);

    try {
      // Query usernames collection by doc ID prefix
      const q = query(
        collection(db, 'usernames'),
        where(documentId(), '>=', prefix),
        where(documentId(), '<=', prefix + '\uf8ff')
      );
      const snap = await getDocs(q);

      // Fetch user profiles for each result
      const results = [];
      for (const usernameDoc of snap.docs) {
        const { uid } = usernameDoc.data();

        // Skip self
        if (user && uid === user.uid) continue;

        // Get user profile for badge info
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          results.push({
            uid,
            username: userData.username,
            activeBadge: userData.activeBadge || 'newbie',
          });
        }

        if (results.length >= 10) break;
      }

      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  }, [user]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    const timer = setTimeout(() => {
      performSearch(searchQuery.trim().toLowerCase());
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, performSearch]);

  // Get relationship status for a search result
  const getResultStatus = useCallback((targetUid) => {
    // Check if already friends
    const isFriend = friends.some((f) => f.uid === targetUid);
    if (isFriend) return 'friends';

    // Check if they sent us a request
    const hasIncoming = requests.some((r) => r.from === targetUid);
    if (hasIncoming) return 'request_received';

    // Check if we sent them a request
    const hasSent = sentRequests.some((r) => r.to === targetUid);
    if (hasSent) return 'request_sent';

    return 'none';
  }, [friends, requests, sentRequests]);

  const handleSendRequest = async (username) => {
    setActionLoading((prev) => ({ ...prev, [username]: true }));
    try {
      await sendFriendRequest(username);
      toast.success(`Friend request sent to ${username}!`);
      // Remove from search results to give feedback
      setSearchResults((prev) => prev.filter((r) => r.username !== username));
    } catch (err) {
      const message = err.message || 'Failed to send request';
      toast.error(message);
    } finally {
      setActionLoading((prev) => ({ ...prev, [username]: false }));
    }
  };

  const handleAcceptRequest = async (requestId, fromUsername) => {
    setActionLoading((prev) => ({ ...prev, [requestId]: true }));
    try {
      await acceptFriendRequest(requestId);
      toast.success(`You are now friends with ${fromUsername}!`);
    } catch (err) {
      toast.error(err.message || 'Failed to accept request');
    } finally {
      setActionLoading((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const handleRejectRequest = async (requestId) => {
    setActionLoading((prev) => ({ ...prev, [requestId]: true }));
    try {
      await rejectFriendRequest(requestId);
      toast.success('Request declined');
    } catch (err) {
      toast.error(err.message || 'Failed to decline request');
    } finally {
      setActionLoading((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const handleCancelRequest = async (requestId, toUsername) => {
    setActionLoading((prev) => ({ ...prev, [requestId]: true }));
    try {
      await cancelFriendRequest(requestId);
      toast.success(`Request to ${toUsername} cancelled`);
    } catch (err) {
      toast.error(err.message || 'Failed to cancel request');
    } finally {
      setActionLoading((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const handleRemoveFriend = async (friendshipId, username) => {
    if (!window.confirm(`Remove ${username} from your friends?`)) return;

    setActionLoading((prev) => ({ ...prev, [friendshipId]: true }));
    try {
      await removeFriend(friendshipId);
      toast.success(`Removed ${username} from friends`);
    } catch (err) {
      toast.error(err.message || 'Failed to remove friend');
    } finally {
      setActionLoading((prev) => ({ ...prev, [friendshipId]: false }));
    }
  };

  return (
    <div className="profile-page">
      <h1>Friends</h1>
      <ProfileSubNav />

      {/* Search Section */}
      <div className="profile-card">
        <div className="friends-search-wrap">
          <span className="friends-search-icon">🔍</span>
          <input
            type="text"
            className="friends-search-input"
            placeholder="Search by username..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {searching && (
          <div className="search-no-results">Searching...</div>
        )}

        {!searching && hasSearched && searchResults.length === 0 && searchQuery.trim().length >= 2 && (
          <div className="search-no-results">No users found</div>
        )}

        {!searching && searchQuery.trim().length === 1 && (
          <div className="search-no-results">Type at least 2 characters to search</div>
        )}

        {searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map((result) => {
              const status = getResultStatus(result.uid);
              return (
                <div key={result.uid} className="search-result-item">
                  <div className="search-result-info">
                    <span className="search-result-emoji">
                      {BADGES[result.activeBadge]?.emoji || '🐣'}
                    </span>
                    <Link to={`/users/${result.username}`} className="search-result-username">
                      {result.username}
                    </Link>
                  </div>
                  {status === 'friends' && (
                    <span className="btn-friends-indicator">Friends ✓</span>
                  )}
                  {status === 'request_received' && (
                    <button
                      className="btn-accept"
                      onClick={() => {
                        const req = requests.find((r) => r.from === result.uid);
                        if (req) handleAcceptRequest(req.id, result.username);
                      }}
                      disabled={actionLoading[result.username]}
                    >
                      Accept
                    </button>
                  )}
                  {status === 'request_sent' && (
                    <span className="btn-pending">Pending</span>
                  )}
                  {status === 'none' && (
                    <button
                      className="btn-add-friend"
                      onClick={() => handleSendRequest(result.username)}
                      disabled={actionLoading[result.username]}
                    >
                      {actionLoading[result.username] ? '...' : 'Add Friend'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pending Requests Section */}
      {!requestsLoading && requests.length > 0 && (
        <div className="profile-card">
          <div className="friends-section-header">
            <h2>Pending Requests</h2>
            <span className="friends-section-count">{requests.length}</span>
          </div>
          {requests.map((req) => (
            <div key={req.id} className="request-item">
              <div className="request-info">
                <span className="request-emoji">👤</span>
                <Link to={`/users/${req.fromUsername}`} className="friend-username-link">
                  {req.fromUsername}
                </Link>
              </div>
              <div className="request-actions">
                <button
                  className="btn-accept"
                  onClick={() => handleAcceptRequest(req.id, req.fromUsername)}
                  disabled={actionLoading[req.id]}
                >
                  {actionLoading[req.id] ? '...' : 'Accept'}
                </button>
                <button
                  className="btn-deny"
                  onClick={() => handleRejectRequest(req.id)}
                  disabled={actionLoading[req.id]}
                >
                  Deny
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Awaiting Response Section */}
      {!sentLoading && sentRequests.length > 0 && (
        <div className="profile-card">
          <div className="friends-section-header">
            <h2>Awaiting Response</h2>
            <span className="friends-section-count">{sentRequests.length}</span>
          </div>
          {sentRequests.map((req) => (
            <div key={req.id} className="request-item">
              <div className="request-info">
                <span className="request-emoji">👤</span>
                <Link to={`/users/${req.toUsername}`} className="friend-username-link">
                  {req.toUsername}
                </Link>
              </div>
              <button
                className="btn-cancel-request"
                onClick={() => handleCancelRequest(req.id, req.toUsername)}
                disabled={actionLoading[req.id]}
              >
                {actionLoading[req.id] ? '...' : 'Cancel'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Friends List Section */}
      <div className="profile-card">
        <div className="friends-section-header">
          <h2>My Friends</h2>
          {friends.length > 0 && (
            <span className="friends-section-count">{friends.length}</span>
          )}
        </div>

        {friendsLoading ? (
          <div className="friends-empty">Loading...</div>
        ) : friends.length === 0 ? (
          <div className="friends-empty">
            <span>👥</span>
            No friends yet. Search for users above!
          </div>
        ) : (
          friends.map((friend) => (
            <div key={friend.friendshipId} className="friend-item">
              <div className="friend-info">
                <span className="friend-emoji">👤</span>
                <Link to={`/users/${friend.username}`} className="friend-username-link">
                  {friend.username}
                </Link>
              </div>
              <button
                className="btn-remove-friend"
                onClick={() => handleRemoveFriend(friend.friendshipId, friend.username)}
                disabled={actionLoading[friend.friendshipId]}
              >
                {actionLoading[friend.friendshipId] ? '...' : 'Remove'}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
